// Kniffe und Eigenschaften als Bilder an den Figuren (Phase Kern, T31, K38; Manifest Version 3 des Grafik-Leads,
// 17.10.2026). Sagt für jeden Moment des Kampfs, welches Bild an welcher Figur hängt und wie alt es ist; zeichnen tut
// figures.ts. Was einen festen Moment hat (ein Kniff, ein Treffer), legt motion.ts fest; was gilt, solange ein Zustand
// wirkt oder ab und zu kommt (die Eigenschaften), steht hier. Reine Darstellung: liest das Ergebnis, schreibt nie hinein.
import { combatRules, systemOn } from '../../content/index.ts';
import { FrameFlag, type BattleResult } from '../../sim/index.ts';
import { fxSeconds, sprite } from '../art.ts';
import { flagsAt, motionOf, movingAt, targetAt } from './motion.ts';

/** Ein Bild an einer Figur in diesem Moment */
export type ActiveFx = {
  kind: 'effect' | 'status';
  id: string;
  /** Sekunden seit Beginn */
  age: number;
  /** Blickrichtung dessen, der es auslöst (Freunde rechts, Sturmwesen links) */
  faceRight: boolean;
  /** am eigenen Körper: dreht sich mit, wenn die Figur im Sprung andersherum schaut */
  self: boolean;
  /** statt des Punkts im Manifest */
  point?: string;
  /** fester Platz am Boden in Feldeinheiten (Erdloch) */
  at?: { x: number; y: number };
  /** K38 Hastig: um den Radius der Figur nach hinten versetzt */
  back?: boolean;
};

/** Anzeigen, die nur ab und zu kommen (pauseMs): das Alter im laufenden Durchlauf oder null in der Pause */
function periodic(id: string, T: number, phase: number): number | null {
  const sp = sprite('status', id);
  if (!sp) return null;
  const run = fxSeconds(sp);
  const cycle = run + (sp.pauseMs ?? 0) / 1000;
  if (cycle <= 0) return null;
  const a = (((T + phase) % cycle) + cycle) % cycle;
  return a < run ? a : null;
}

/** Alle Bilder an den Figuren zur Zeit T, nach uid */
export function fxAt(r: BattleResult, T: number): Map<number, ActiveFx[]> {
  const out = new Map<number, ActiveFx[]>();
  const add = (uid: number, f: ActiveFx) => {
    if (!sprite(f.kind, f.id)) return;
    const list = out.get(uid);
    if (list) list.push(f);
    else out.set(uid, [f]);
  };
  const right = (uid: number) => r.units[uid]?.side === 'friend';
  for (const f of motionOf(r).fx) {
    if (T < f.t0 || T > f.t1) continue;
    add(f.unit, { kind: f.kind, id: f.id, age: T - f.t0, faceRight: right(f.owner), self: f.owner === f.unit, ...(f.point ? { point: f.point } : {}), ...(f.at ? { at: f.at } : {}) });
  }
  for (const u of r.units) {
    if (u.kind === 'tree') continue;
    const flags = flagsAt(r, u.uid, T);
    if ((flags & FrameFlag.alive) === 0) continue;
    const me = { faceRight: u.side === 'friend', self: true };
    const phase = u.uid * 0.93;
    // Version 5: Wer eine Schwachstelle hat, trägt die Zielscheibe (flag marked), solange sie gilt
    if (flags & FrameFlag.marked) add(u.uid, { kind: 'status', id: 'schwachstelle-ziel', age: T + phase, faceRight: true, self: false });
    // G24 Grollen: Wessen Angriffe zaghaft sind, zeigt es über dem Kopf
    if (flags & FrameFlag.timid) add(u.uid, { kind: 'effect', id: 'zaghaft', age: T, ...me, self: false });
    // K41, Version 4: Trotz der Raufbolde als Schnaufwölkchen am Kopf, je weniger Leben, desto öfter und kräftiger
    if (u.friendClass === 'raufbold' && systemOn('raufboldTrotz')) {
      const id = trotzAt(r, u.uid, T);
      const a = id ? periodic(id, T, phase) : null;
      if (id && a !== null) add(u.uid, { kind: 'status', id, age: a, faceRight: u.side === 'friend', self: true });
    }
    for (const id of u.traits) {
      if (id === 'tueckisch') {
        const a = periodic('anzeige-tueckisch', T, phase);
        if (a !== null) add(u.uid, { kind: 'status', id: 'anzeige-tueckisch', age: a, ...me });
      } else if (id === 'hastig') {
        add(u.uid, { kind: 'status', id: movingAt(r, u.uid, T) ? 'anzeige-hastig' : 'anzeige-hastig-stand', age: T + phase, ...me, back: true });
      } else if (id === 'feige') {
        if (flags & FrameFlag.fleeing) {
          // auf der Flucht ohne Pause, mit Zitterstrichen und der kleinen weißen Fahne
          for (const f of ['anzeige-feige', 'anzeige-feige-flucht', 'anzeige-feige-fahne']) add(u.uid, { kind: 'status', id: f, age: T, ...me });
        } else {
          const a = periodic('anzeige-feige', T, phase);
          if (a !== null) add(u.uid, { kind: 'status', id: 'anzeige-feige', age: a, ...me });
        }
      } else if (id === 'zaeh') {
        const a = periodic('anzeige-zaeh', T, phase);
        if (a !== null) add(u.uid, { kind: 'status', id: 'anzeige-zaeh', age: a, ...me });
      } else if (id === 'wuetend') {
        if (flags & FrameFlag.enraged) add(u.uid, { kind: 'status', id: 'anzeige-wuetend', age: T, ...me });
      } else if (id === 'traege') {
        const a = periodic('anzeige-traege', T, phase);
        if (a !== null) add(u.uid, { kind: 'status', id: 'anzeige-traege', age: a, ...me });
      } else if (id === 'fanatisch') {
        add(u.uid, { kind: 'status', id: 'anzeige-fanatisch', age: T + phase, ...me });
        // die Klammer auf dem Ziel, das es nicht loslässt
        const tgt = targetAt(r, u.uid, T);
        if (tgt >= 0 && (flagsAt(r, tgt, T) & FrameFlag.alive) !== 0) add(tgt, { kind: 'status', id: 'anzeige-fanatisch-ziel', age: T + phase, faceRight: me.faceRight, self: false });
      }
    }
  }
  return out;
}

/** K41, Version 4: welche Stufe der Trotz-Wölkchen gerade gilt (trotz-1 ab 75 % Leben und darunter, -2 ab 50 %, -3 ab 25 %) */
function trotzAt(r: BattleResult, uid: number, T: number): string | null {
  const u = r.units[uid]!;
  const f = r.frames[Math.max(0, Math.min(r.frames.length - 1, Math.round(T / combatRules.tickSeconds)))];
  const hp = f?.units[uid]?.[2];
  if (hp === undefined) return null;
  const share = hp / u.maxHp;
  let best: string | null = null;
  let below = Infinity;
  for (const id of ['trotz-1', 'trotz-2', 'trotz-3']) {
    const sp = sprite('status', id);
    if (sp?.lifeBelow !== undefined && share <= sp.lifeBelow && sp.lifeBelow < below) {
      best = id;
      below = sp.lifeBelow;
    }
  }
  return best;
}

/** T30, K38: Ob eine Figur in diesem Moment wackelt (Feige auf der Flucht): -1, 0 oder 1 Bildpixel */
export function wobbleAt(r: BattleResult, uid: number, T: number): number {
  const u = r.units[uid];
  if (!u || !u.traits.includes('feige') || !sprite('status', 'anzeige-feige-flucht')) return 0;
  if ((flagsAt(r, uid, T) & FrameFlag.fleeing) === 0) return 0;
  return Math.floor(T * 12) % 2 === 0 ? -1 : 1;
}
